// Geo localization helpers for analytics.
// Countries are localized from their ISO country_code via the browser's
// built-in Intl.DisplayNames (supports en/ru/es). Cities use the city_i18n
// map provided by the backend (from MaxMind GeoLite2), falling back to the
// canonical name.

const UNKNOWN = { en: "Unknown", ru: "Неизвестно", es: "Desconocido" };

// City name dictionary for localization fallback.
// Used when an analytics item has no city_i18n (e.g. historical records stored
// the city as a single localized string). Each entry: [en, ru, es].
// Keyed (case-insensitively) by both the English and Russian names.
const CITY_TABLE = [
  // Kazakhstan
  ["Almaty", "Алматы", "Almatý"],
  ["Astana", "Астана", "Astaná"],
  ["Nur-Sultan", "Нур-Султан", "Nur-Sultán"],
  ["Shymkent", "Шымкент", "Shymkent"],
  ["Karaganda", "Караганда", "Karagandá"],
  ["Aktobe", "Актобе", "Aktóbe"],
  ["Taraz", "Тараз", "Taraz"],
  ["Pavlodar", "Павлодар", "Pavlodar"],
  ["Oskemen", "Усть-Каменогорск", "Oskemen"],
  ["Ust-Kamenogorsk", "Усть-Каменогорск", "Ust-Kamenogorsk"],
  ["Semey", "Семей", "Semey"],
  ["Atyrau", "Атырау", "Atyrau"],
  ["Kostanay", "Костанай", "Kostanay"],
  ["Kyzylorda", "Кызылорда", "Kyzylorda"],
  ["Oral", "Уральск", "Oral"],
  ["Petropavl", "Петропавловск", "Petropavl"],
  ["Aktau", "Актау", "Aktau"],
  ["Temirtau", "Темиртау", "Temirtau"],
  ["Turkestan", "Туркестан", "Turkestán"],
  ["Kokshetau", "Кокшетау", "Kokshetau"],
  ["Taldykorgan", "Талдыкорган", "Taldykorgan"],
  ["Ekibastuz", "Экибастуз", "Ekibastuz"],
  // Russia
  ["Moscow", "Москва", "Moscú"],
  ["Saint Petersburg", "Санкт-Петербург", "San Petersburgo"],
  ["Novosibirsk", "Новосибирск", "Novosibirsk"],
  ["Yekaterinburg", "Екатеринбург", "Ekaterimburgo"],
  ["Kazan", "Казань", "Kazán"],
  ["Nizhny Novgorod", "Нижний Новгород", "Nizhni Nóvgorod"],
  ["Chelyabinsk", "Челябинск", "Cheliábinsk"],
  ["Samara", "Самара", "Samara"],
  ["Omsk", "Омск", "Omsk"],
  ["Rostov-on-Don", "Ростов-на-Дону", "Rostov del Don"],
  ["Ufa", "Уфа", "Ufá"],
  ["Krasnoyarsk", "Красноярск", "Krasnoyarsk"],
  ["Voronezh", "Воронеж", "Vorónezh"],
  ["Perm", "Пермь", "Perm"],
  ["Volgograd", "Волгоград", "Volgogrado"],
  ["Krasnodar", "Краснодар", "Krasnodar"],
  ["Saratov", "Саратов", "Sarátov"],
  ["Tyumen", "Тюмень", "Tiumén"],
  ["Sochi", "Сочи", "Sochi"],
  ["Vladivostok", "Владивосток", "Vladivostok"],
  ["Kaliningrad", "Калининград", "Kaliningrado"],
  ["Izhevsk", "Ижевск", "Izhevsk"],
  ["Irkutsk", "Иркутск", "Irkutsk"],
  ["Khabarovsk", "Хабаровск", "Jabárovsk"],
  // Ukraine / Belarus
  ["Kyiv", "Киев", "Kiev"],
  ["Kharkiv", "Харьков", "Járkov"],
  ["Odesa", "Одесса", "Odesa"],
  ["Dnipro", "Днепр", "Dnipró"],
  ["Lviv", "Львов", "Leópolis"],
  ["Minsk", "Минск", "Minsk"],
  ["Gomel", "Гомель", "Gómel"],
  // World majors
  ["London", "Лондон", "Londres"],
  ["New York", "Нью-Йорк", "Nueva York"],
  ["Los Angeles", "Лос-Анджелес", "Los Ángeles"],
  ["Paris", "Париж", "París"],
  ["Berlin", "Берлин", "Berlín"],
  ["Madrid", "Мадрид", "Madrid"],
  ["Rome", "Рим", "Roma"],
  ["Istanbul", "Стамбул", "Estambul"],
  ["Dubai", "Дубай", "Dubái"],
  ["Tokyo", "Токио", "Tokio"],
  ["Warsaw", "Варшава", "Varsovia"],
  ["Amsterdam", "Амстердам", "Ámsterdam"],
  ["Prague", "Прага", "Praga"],
  ["Tashkent", "Ташкент", "Taskent"],
  ["Baku", "Баку", "Bakú"],
  ["Tbilisi", "Тбилиси", "Tiflis"],
  ["Yerevan", "Ереван", "Ereván"],
  ["Bishkek", "Бишкек", "Biskek"],
];

const CITY_MAP = {};
for (const [en, ru, es] of CITY_TABLE) {
  const entry = { en, ru, es };
  CITY_MAP[en.toLowerCase()] = entry;
  CITY_MAP[ru.toLowerCase()] = entry;
}

// Cache DisplayNames instances per language
const _displayNames = {};

function getDisplayNames(lang) {
  const key = lang || "en";
  if (_displayNames[key] === undefined) {
    try {
      _displayNames[key] = new Intl.DisplayNames([key], { type: "region" });
    } catch {
      _displayNames[key] = null;
    }
  }
  return _displayNames[key];
}

/** Flag emoji from a 2-letter ISO country code (e.g. "RU" -> 🇷🇺). */
export function countryFlag(code) {
  if (!code || code.length !== 2) return "🌍";
  const cc = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return "🌍";
  return cc.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

/**
 * Localized country name from an ISO code.
 * @param {string} code - ISO 3166-1 alpha-2 code
 * @param {string} lang - app language (en/ru/es)
 * @param {string} fallback - fallback name if code is missing/unknown
 */
export function localizeCountry(code, lang = "en", fallback = "") {
  if (!code) return fallback || UNKNOWN[lang] || UNKNOWN.en;
  const dn = getDisplayNames(lang);
  if (dn) {
    try {
      const name = dn.of(code.toUpperCase());
      if (name && name !== code.toUpperCase()) return name;
    } catch {
      /* ignore */
    }
  }
  return fallback || code;
}

/**
 * Localized city name from an analytics item.
 * @param {{city?: string, city_i18n?: object}} item
 * @param {string} lang - app language (en/ru/es)
 */
export function localizeCity(item, lang = "en") {
  if (!item) return UNKNOWN[lang] || UNKNOWN.en;
  const i18n = item.city_i18n || {};
  // 1) Prefer per-event localized names from the backend (MaxMind)
  let name = i18n[lang] || i18n.en;
  // 2) Fallback: translate a single stored city string via the dictionary
  if (!name && item.city) {
    const entry = CITY_MAP[item.city.trim().toLowerCase()];
    if (entry) name = entry[lang] || entry.en;
  }
  // 3) Last resort: the raw stored name
  if (!name) name = item.city;
  if (!name || name === "Неизвестно" || name === "Unknown") {
    return UNKNOWN[lang] || UNKNOWN.en;
  }
  return name;
}
